import { useCallback, useEffect, useState } from 'react';
import {
    fetchDevices, fetchDevice, putDeviceMetadata,
    fetchForecast, fetchHeatingStatus, fetchMeasurements, fetchSolaxData, fetchSolarForecast,
} from '../api/portal';
import { filterToLocalDay, localToday } from '../utils/dateFormatting';

const EMPTY_INFO = { friendly_name: '', description: '', latitude: '', longitude: '' };

// Devices the user can see, the selected one, and its metadata.
export function useDevices() {
    const [devices, setDevices] = useState([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [deviceInfo, setDeviceInfo] = useState(EMPTY_INFO);
    const [error, setError] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        fetchDevices()
            .then(list => {
                setDevices(list);
                // With one device there is nothing to choose.
                if (list.length === 1) setSelectedDeviceId(list[0].device_id);
            })
            .catch(e => setError(`Could not load devices: ${e.message}`))
            .finally(() => setLoaded(true));
    }, []);

    useEffect(() => {
        if (!selectedDeviceId) { setDeviceInfo(EMPTY_INFO); return; }
        fetchDevice(selectedDeviceId)
            .then(d => setDeviceInfo({ ...EMPTY_INFO, ...(d?.metadata || {}) }))
            .catch(e => {
                setDeviceInfo(EMPTY_INFO);
                setError(`Could not load device details: ${e.message}`);
            });
    }, [selectedDeviceId]);

    // `changes` holds only the fields the user filled in.
    const saveDevice = useCallback(async (changes) => {
        const vars = { device_id: selectedDeviceId };
        ['friendly_name', 'description'].forEach(k => { if (changes[k]) vars[k] = changes[k]; });
        ['latitude', 'longitude'].forEach(k => { if (changes[k] !== undefined && changes[k] !== '') vars[k] = parseFloat(changes[k]); });
        try {
            const updated = await putDeviceMetadata(vars);
            setDeviceInfo({ ...EMPTY_INFO, ...updated });
            setError(null);
            return true;
        } catch (e) {
            setError(`Could not update the device: ${e.message}`);
            return false;
        }
    }, [selectedDeviceId]);

    return { devices, loaded, selectedDeviceId, setSelectedDeviceId, deviceInfo, saveDevice, error };
}

// Everything the day view shows for one device and one London day.
// Sensing-only sites (showForecast=false) get measured readings; sites with
// control get the thermal model's forecast, which also carries the measured
// room temperature. Either way heating on/off comes with it.
// `scheduleVersion` changes after a re-solve so the forecast refetches.
export function useDayData({ deviceId, date, scheduleVersion, showSolar = true, showForecast = true }) {
    const [forecast, setForecast] = useState([]);
    const [measurements, setMeasurements] = useState([]);
    const [heatingStatus, setHeatingStatus] = useState([]);
    const [loading, setLoading] = useState(false);
    const [solax, setSolax] = useState([]);
    const [solarForecast, setSolarForecast] = useState([]);
    const [errors, setErrors] = useState({});

    const setErr = (key, e) => setErrors(prev => ({ ...prev, [key]: e ? e.message : null }));

    useEffect(() => {
        if (!deviceId) { setForecast([]); setMeasurements([]); setHeatingStatus([]); return; }
        let cancelled = false;
        setLoading(true);
        const readings = showForecast ? fetchForecast(deviceId, date) : fetchMeasurements(deviceId, date);
        Promise.all([fetchHeatingStatus(deviceId, date), readings])
            .then(([status, rows]) => {
                if (cancelled) return;
                setHeatingStatus(status);
                setForecast(showForecast ? rows : []);
                setMeasurements(showForecast ? [] : filterToLocalDay(rows, date));
                setErr('temperature', null);
            })
            .catch(e => { if (!cancelled) setErr('temperature', e); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [deviceId, date, scheduleVersion, showForecast]);

    useEffect(() => {
        if (!showSolar) { setSolax([]); return; }
        let cancelled = false;
        fetchSolaxData(date)
            .then(rows => { if (!cancelled) { setSolax(filterToLocalDay(rows, date)); setErr('solax', null); } })
            .catch(e => { if (!cancelled) setErr('solax', e); });
        return () => { cancelled = true; };
    }, [date, showSolar]);

    // The weather forecast only covers today onwards, so past days have no solar forecast.
    useEffect(() => {
        if (!showSolar || !deviceId || date < localToday()) { setSolarForecast([]); return; }
        let cancelled = false;
        fetchSolarForecast(deviceId, date)
            .then(rows => { if (!cancelled) { setSolarForecast(filterToLocalDay(rows, date)); setErr('solarForecast', null); } })
            .catch(e => { if (!cancelled) setErr('solarForecast', e); });
        return () => { cancelled = true; };
    }, [deviceId, date, showSolar]);

    return { forecast, measurements, heatingStatus, loading, solax, solarForecast, errors };
}
